import { useCallback, useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Download, FileText, ListChecks } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

/**
 * What people actually answered.
 *
 * A long registration form collects a great deal and, until now, none of it was
 * readable anywhere: the answers sat in the database with no screen.
 */

interface Field {
  fieldId?: string;
  fieldName: string;
  fieldLabel: string;
  fieldType: string;
  options?: string[];
  section?: string;
}

interface ResponseRow {
  registrationId: string;
  name: string;
  email: string;
  status: string;
  paid: boolean;
  registeredAt?: string;
  answers: Record<string, unknown>;
}

interface EventOption { _id: string; title: string }

const CHOICE_TYPES = ['select', 'radio', 'multiselect', 'checkbox'];

const answerOf = (row: ResponseRow, field: Field) => {
  const byId = field.fieldId ? row.answers[field.fieldId] : undefined;
  return byId !== undefined ? byId : row.answers[field.fieldName];
};

const asText = (v: unknown) =>
  Array.isArray(v) ? v.join(', ') : v === true ? 'Yes' : v === false ? 'No' : v == null ? '' : String(v);

export default function ResponsesPanel({ events }: { events: EventOption[] }) {
  const { error } = useToast();
  const [eventId, setEventId] = useState('');
  const [fields, setFields] = useState<Field[]>([]);
  const [rows, setRows] = useState<ResponseRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<'summary' | 'table'>('summary');

  const load = useCallback(async (id: string) => {
    if (!id) { setFields([]); setRows([]); return; }
    setLoading(true);
    try {
      const res = await api.get(`/registrations/admin/responses/${id}`);
      setFields(res.data.fields || []);
      setRows(res.data.responses || []);
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not load the responses');
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => { load(eventId); }, [eventId, load]);

  /** Counts per option, for the questions where counting means something. */
  const summaries = useMemo(() => fields.map((field) => {
    const answered = rows.filter((r) => {
      const v = answerOf(r, field);
      return v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
    });

    if (CHOICE_TYPES.includes(field.fieldType)) {
      const counts = new Map<string, number>();
      for (const opt of field.options || []) counts.set(opt, 0);
      for (const r of answered) {
        const v = answerOf(r, field);
        const picked = Array.isArray(v) ? v : [asText(v)];
        for (const p of picked) {
          if (!p) continue;
          counts.set(String(p), (counts.get(String(p)) || 0) + 1);
        }
      }
      return {
        field,
        kind: 'chart' as const,
        answered: answered.length,
        data: [...counts.entries()].map(([name, value]) => ({ name, value }))
      };
    }

    return {
      field,
      kind: 'text' as const,
      answered: answered.length,
      samples: answered.map((r) => ({ who: r.name, text: asText(answerOf(r, field)) })).filter((s) => s.text)
    };
  }), [fields, rows]);

  const exportCsv = () => {
    const head = ['Name', 'Email', 'Status', 'Paid', 'Registered', ...fields.map((f) => f.fieldLabel)];
    const lines = rows.map((r) => [
      r.name, r.email, r.status, r.paid ? 'Yes' : 'No',
      r.registeredAt ? new Date(r.registeredAt).toLocaleString('en-IN') : '',
      ...fields.map((f) => asText(answerOf(r, f)))
    ].map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','));

    const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `responses-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const sections = useMemo(() => {
    const names = fields.map((f) => f.section?.trim()).filter(Boolean) as string[];
    return [...new Set(names)];
  }, [fields]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[260px]">
          <label htmlFor="resp-event" className="mb-1 block text-sm font-medium text-gray-700">Event</label>
          <select
            id="resp-event" value={eventId} onChange={(e) => setEventId(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2"
          >
            <option value="">Choose an event…</option>
            {events.map((e) => <option key={e._id} value={e._id}>{e.title}</option>)}
          </select>
        </div>

        {eventId && rows.length > 0 && (
          <>
            <div className="flex gap-2">
              <button type="button" onClick={() => setView('summary')}
                className={`rounded-md px-3 py-2 text-sm font-medium ${view === 'summary' ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-700'}`}>
                <ListChecks className="mr-1.5 inline h-4 w-4" /> Summary
              </button>
              <button type="button" onClick={() => setView('table')}
                className={`rounded-md px-3 py-2 text-sm font-medium ${view === 'table' ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-700'}`}>
                <FileText className="mr-1.5 inline h-4 w-4" /> Every response
              </button>
            </div>
            <button type="button" onClick={exportCsv}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </>
        )}
      </div>

      {loading && <p className="py-10 text-center text-gray-500">Loading…</p>}

      {!loading && eventId && rows.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-white py-12 text-center">
          <p className="text-gray-700">No responses yet for this event.</p>
          <p className="mt-1 text-sm text-gray-500">
            {fields.length === 0
              ? 'This event has no registration questions.'
              : `The form has ${fields.length} question${fields.length === 1 ? '' : 's'}, but nobody has registered.`}
          </p>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-sm text-gray-500">Responses</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{rows.length}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-sm text-gray-500">Questions</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{fields.length}</p>
              {sections.length > 0 && <p className="text-xs text-gray-500">{sections.length} section{sections.length === 1 ? '' : 's'}</p>}
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-sm text-gray-500">Paid</p>
              <p className="mt-1 text-2xl font-semibold text-green-700">{rows.filter((r) => r.paid).length}</p>
              <p className="text-xs text-gray-500">of {rows.length}</p>
            </div>
          </div>

          {view === 'summary' && (
            <div className="space-y-4">
              {summaries.map((s) => (
                <div key={s.field.fieldId || s.field.fieldName} className="rounded-lg border border-gray-200 bg-white p-5">
                  <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                    <div>
                      <h3 className="font-semibold text-gray-900">{s.field.fieldLabel}</h3>
                      {s.field.section && <p className="text-xs text-gray-500">{s.field.section}</p>}
                    </div>
                    <p className="text-sm text-gray-500">
                      {s.answered} of {rows.length} answered
                    </p>
                  </div>

                  {s.kind === 'chart' && s.data.length > 0 && (
                    <div style={{ height: Math.max(140, s.data.length * 42) }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={s.data} layout="vertical" margin={{ left: 8, right: 24 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" allowDecimals={false} />
                          <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Bar dataKey="value" fill="#2563eb" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {s.kind === 'chart' && s.data.length === 0 && (
                    <p className="text-sm text-gray-500">No choices recorded.</p>
                  )}

                  {s.kind === 'text' && (
                    s.samples.length === 0
                      ? <p className="text-sm text-gray-500">Nobody answered this.</p>
                      : <ul className="max-h-72 space-y-2 overflow-y-auto">
                          {s.samples.map((x, i) => (
                            <li key={i} className="rounded-md bg-gray-50 p-3 text-sm">
                              <span className="text-gray-900">{x.text}</span>
                              <span className="mt-0.5 block text-xs text-gray-500">— {x.who}</span>
                            </li>
                          ))}
                        </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          {view === 'table' && (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="whitespace-nowrap px-4 py-3 font-semibold">Person</th>
                    <th className="whitespace-nowrap px-4 py-3 font-semibold">Paid</th>
                    {fields.map((f) => (
                      <th key={f.fieldId || f.fieldName} className="whitespace-nowrap px-4 py-3 font-semibold">{f.fieldLabel}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.registrationId} className="border-b border-gray-100 last:border-0 align-top">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{r.name}</div>
                        <div className="text-xs text-gray-500">{r.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${r.paid ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                          {r.paid ? 'Yes' : 'No'}
                        </span>
                      </td>
                      {fields.map((f) => (
                        <td key={f.fieldId || f.fieldName} className="max-w-xs px-4 py-3 text-gray-700">
                          {asText(answerOf(r, f)) || <span className="text-gray-300">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
